import type {PropertyValues} from 'lit';
import {html, LitElement} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import {scrollToTopSmooth} from '../../helpers/dom.ts';
import {Tooltip} from 'flowbite';
import {easeOut} from 'ol/easing.js';
import type {RouteLayerInfo} from '../../layers/layerTypes.ts';
import {isSummerLayer, isWinterLayer} from '../../layers/layerHelpers.ts';
import {ROUTE_LAYERS} from '../../layers/layerConstants.ts';

import type {Map} from 'ol';
import {msg} from '@lit/localize';
import {
  layersSvg,
  minusSvg,
  closeMapActionSvg,
  openMapActionSvg,
  plusSvg,
  targetSvg,
  layersSelectedSvg,
} from '../../helpers/elements.ts';
import {classMap} from 'lit/directives/class-map.js';
import {disableMapAnimation$, modal$} from '../../stores/store-main.ts';
import {sendAnalytics} from '../../analytics.ts';
import {unlistenByKey} from 'ol/events.js';
import './map-element-share-or-print-popup.ts';
import {
  map$,
  visibleLayers$,
  geolocationHelper$,
} from '../../stores/store-map.ts';
import type {Subscription} from 'rxjs';
import {sharePopupOpen$} from '../../stores/store-share-tour.ts';
import type {GeolocationHelper} from '../../map/geolocationHelper.ts';

@customElement('element-map-buttons')
export class ElementMapButtons extends LitElement {
  @state() private map: Map | undefined;
  @property({type: Boolean}) open = !disableMapAnimation$.getValue();
  @property({type: Boolean}) popupAllowed = false;
  @property({type: Object}) scrollContainer: HTMLElement;
  @property({type: Boolean}) showLayerTooltip = true;
  @state() private activeLayer: RouteLayerInfo | undefined;
  @state() geolocationActive = false;
  @state() openButtons = disableMapAnimation$.getValue();
  @state() hideButtons = disableMapAnimation$.getValue();
  @state() collapseButtons = disableMapAnimation$.getValue();
  @query('.map-btns-container') buttonsContainer: HTMLElement;
  @state() layersSwitchedOn = false;

  private zoomDuration = 200;
  private geolocationHelper: GeolocationHelper;

  private layerTooltip: Tooltip | undefined;

  private subscriptions: Subscription[] = [];

  override connectedCallback() {
    this.subscriptions.push(
      map$.subscribe((map) => {
        this.map = map;
      }),
      geolocationHelper$.subscribe((geolocationHelper: GeolocationHelper) => {
        this.geolocationHelper = geolocationHelper;
      }),
    );
    this.subscriptions.push(
      sharePopupOpen$.subscribe((sharePopupOpen) => {
        if (sharePopupOpen && !this.open) {
          this.open = true;
          this.popupAllowed = false;
          scrollToTopSmooth();
        }
      }),
    );
    const activeAPICode = visibleLayers$.getValue();

    Object.values(ROUTE_LAYERS).forEach((layerInfo: RouteLayerInfo) => {
      if (activeAPICode.includes(layerInfo.layer)) this.activeLayer = layerInfo;
    });
    super.connectedCallback();
  }

  override disconnectedCallback() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.disconnectedCallback();
  }

  createTooltip() {
    return html` <div
      role="tooltip"
      class="position-tooltip layer-tooltip ${this.activeLayer
        .pageColorClass} absolute z-40 inline-block flex w-[300px] gap-[12px] rounded p-[12px] shadow-card transition-opacity duration-[800ms] ease-in-out md:w-[355px] lg:w-[463px]"
    >
      <div class="tooltip-arrow -right-[4px]" data-popper-arrow></div>
      <div class="h-[56px] lg:h-[80px]">
        <div
          class="${this.activeLayer
            .logoClass} h-logo-m w-logo-m bg-cover bg-no-repeat lg:h-logo-l lg:w-logo-l"
        ></div>
      </div>
      <div
        class="flex h-full w-full flex-col justify-center text-body md:text-md-body lg:text-lg-body"
      >
        <p class="font-bold md:leading-tight">
          ${this.activeLayer.getLayerHintTitle()}
        </p>
        <p class="">${msg('Show more map contents')}</p>
      </div>
    </div>`;
  }

  override render() {
    return html`
      <div
        class="map-btns-container absolute bottom-[16px] right-[20px] z-40 flex flex-col items-center md:bottom-[32px] mdxx:right-[32px]"
      >
        ${this.activeLayer && this.showLayerTooltip ? this.createTooltip() : ''}
        <button
          @click="${() => {
            modal$.next({type: 'layers'});
            scrollToTopSmooth();
          }}"
          type="button"
          aria-label="${msg('Map contents')}"
          class="btn-layers flex items-end justify-start mb-[16px] mdxx:mb-[24px] md:mb-[20px] rounded-full bg-chm-red shadow-card mouse:hover:bg-chm-focus-red active:bg-chm-focus-red"
        >
          ${this.layersSwitchedOn
            ? layersSelectedSvg('btn-layers-icon')
            : layersSvg('btn-layers-icon')}
        </button>
        <div
          class="fade-in-out-animation-h ${classMap({
            'h-0': this.collapseButtons,
            'h-[202px]': !this.collapseButtons,
            'mdxx:h-[266px]': !this.collapseButtons,
            'mb-[16px]': !this.collapseButtons,
            'md:mb-[20px]': !this.collapseButtons,
            'mdxx:mb-[24px]': !this.collapseButtons,
          })}"
        >
          <div
            class="fade-in-out-animation-o ${classMap({
              hidden: this.hideButtons,
              'opacity-0': this.openButtons,
              'opacity-100': !this.openButtons,
            })} flex h-[202px] flex-col gap-[16px]  w-nav-button-s mdxx:w-nav-button-l mdxx:h-[266px]"
            @click="${scrollToTopSmooth}"
          >
            <element-share-or-print-popup
              .showMask="${this.open}"
              .popupShown="${this.popupAllowed}"
              .selectable="${!this.hideButtons}"
              @togglePopup=${this._popupEvent}
              id="element-share-or-print-popup"
            ></element-share-or-print-popup>
            <button
              @click="${() => this.toggleGeolocation()}"
              tabindex="${this.hideButtons ? -1 : 0}"
              type="button"
              aria-label="${msg('Localise')}"
              class="${classMap({
                'bg-white': !this.geolocationActive,
                'bg-chm-focus-red': this.geolocationActive,
              })} group rounded-full p-2.5 shadow-card active:bg-chm-focus-red mouse:hover:bg-chm-focus-red"
            >
              ${targetSvg(
                `${
                  this.geolocationActive ? 'stroke-white' : 'stroke-chm-red'
                } group-active:stroke-white mouse:group-hover:stroke-white`,
              )}
            </button>
            <button
              @click="${() => this.zoomByDelta(+1)}"
              tabindex="${this.hideButtons ? -1 : 0}"
              type="button"
              aria-label="${msg('Zoom in')}"
              class="group rounded-full bg-white p-2.5 shadow-card active:bg-chm-focus-red mouse:hover:bg-chm-focus-red"
            >
              ${plusSvg(
                'fill-chm-red group-active:fill-white mouse:group-hover:fill-white',
              )}
            </button>
            <button
              @click="${() => this.zoomByDelta(-1)}"
              tabindex="${this.hideButtons ? -1 : 0}"
              type="button"
              aria-label="${msg('Zoom out')}"
              class="group group rounded-full bg-white p-2.5 shadow-card active:bg-chm-focus-red mouse:hover:bg-chm-focus-red"
            >
              ${minusSvg(
                'fill-chm-red group-active:fill-white  mouse:group-hover:fill-white',
              )}
            </button>
          </div>
        </div>
        <button
          tabindex="${this.hideButtons ? -1 : 0}"
          @click="${() => {
            this.open = false;
            sharePopupOpen$.next(false);
          }}"
          type="button"
          aria-label="${msg('Close')}"
          class="items-center justify-center z-50 rounded-full bg-chm-red shadow-card w-nav-button-big-s md:w-nav-button-big-m mdxx:w-nav-button-big-l h-nav-button-big-s md:h-nav-button-big-m mdxx:h-nav-button-big-l mouse:hover:bg-chm-focus-red ${classMap(
            {
              hidden: !this.open,
              flex: this.open,
            },
          )}"
        >
          ${closeMapActionSvg('h-[18px] md:h-[25px] mdxx:h-[36px]')}
        </button>
        <button
          @click="${() => {
            this.open = true;
            this.popupAllowed = false;
            scrollToTopSmooth();
          }}"
          type="button"
          aria-label="${msg('Map operations')}"
          class="items-center justify-center z-50 rounded-full bg-chm-red shadow-card w-nav-button-big-s md:w-nav-button-big-m mdxx:w-nav-button-big-l h-nav-button-big-s md:h-nav-button-big-m mdxx:h-nav-button-big-l mouse:hover:bg-chm-focus-red ${classMap(
            {
              hidden: this.open,
              flex: !this.open,
            },
          )}"
        >
          ${openMapActionSvg('h-[18px] md:h-[24px] mdxx:h-[36px]')}
        </button>
      </div>
    `;
  }

  override firstUpdated() {
    setTimeout(() => (this.open = false), 800);
    visibleLayers$.subscribe((layers) => {
      this.layersSwitchedOn = layers.some(
        (layer) => isSummerLayer(layer) || isWinterLayer(layer),
      );
    });

    if (this.layersSwitchedOn && this.showLayerTooltip) {
      this.layerTooltip = new Tooltip(
        this.querySelector('.layer-tooltip'),
        this.querySelector('.btn-layers'),
        {
          placement: 'left',
          triggerType: 'none',
          onShow: () => {
            setTimeout(() => this.layerTooltip.hide(), 3000);
          },
        },
      );
      setTimeout(() => this.layerTooltip.show(), this.open ? 2000 : 300);
    }
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('open')) this.toggleButtons();
  }

  private _popupEvent(e: CustomEvent) {
    this.popupAllowed = e.detail;
  }

  toggleGeolocation() {
    this.geolocationHelper.tracking = !this.geolocationHelper.tracking;
    this.geolocationActive = !this.geolocationActive;
    if (this.geolocationActive) {
      sendAnalytics('event', 'geolocation');
    }
    // disable tracking when user moves the map, zooming the map does not disable it
    if (this.geolocationHelper.tracking) {
      const moveStartListener = this.map.on('movestart', () => {
        if (this.map.getView().getInteracting()) {
          this.toggleGeolocation();
          unlistenByKey(moveStartListener);
        }
      });
    }
  }

  zoomByDelta(delta: number) {
    const view = this.map.getView();
    const currentZoom = view.getZoom();
    if (currentZoom !== undefined) {
      if (view.getAnimating()) {
        view.cancelAnimations();
      }
      view.animate({
        zoom: view.getConstrainedZoom(currentZoom + delta),
        duration: this.zoomDuration,
        easing: easeOut,
      });
    }
  }

  toggleButtons() {
    if (this.open) {
      if (!disableMapAnimation$.getValue()) disableMapAnimation$.next(true);
      this.collapseButtons = !this.open;
      this.scrollContainer?.scroll({left: 0, top: 0, behavior: 'smooth'});
      this.hideButtons = !this.open;
      setTimeout(() => {
        this.openButtons = !this.open;
      }, 500);
    } else {
      this.openButtons = !this.open;
      setTimeout(() => {
        this.hideButtons = !this.open;
        this.collapseButtons = !this.open;
      }, 300);
    }
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-map-buttons': ElementMapButtons;
  }
}
